/**
 * Execution detail slice — data for the /execution page.
 *
 * - fetchExecutionDetail: POST /executions/detail via beaconApi, mapped to the
 *   ExecutionDetailView view model. When several executions match, the one whose
 *   id equals the searched id is shown, else the latest (last by createdAt).
 *
 * Runs against the selected region, or every region in a global search.
 * `data` holds the active region's hit; `hits` carries the rest.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ExecutionDetailView } from './executionDetailTypes';
import { mapExecutionDetail } from './executionDetailMapper';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';
import {
  FanOutResult, RegionLookupState, describeFailures, fanOutByRegion,
  regionBases, regionLookupInitialState,
} from '../regionFanOut';

export interface ExecutionDetailState extends RegionLookupState<ExecutionDetailView> {
  data: ExecutionDetailView | null;
  /** Other executions returned for the same identifier, in the active region. */
  otherExecutionIds: string[];
  loading: boolean;
  error: string | null;
  notFound: boolean;
  currentRequestId: string | null;
  /** Entity id of the last fetch; re-dispatched on region change. */
  currentId: string | null;
}

export const executionDetailInitialState: ExecutionDetailState = {
  data: null,
  otherExecutionIds: [],
  loading: false,
  error: null,
  notFound: false,
  currentRequestId: null,
  currentId: null,
  ...regionLookupInitialState,
};

export const fetchExecutionDetail = createAsyncThunk<
  FanOutResult<ExecutionDetailView>, string, { state: RootState; rejectValue: string }
>('executionDetail/fetch', async (executionId, { getState }) => {
  const lookup = async (apiBase: string) => {
    const response = await beaconApi.getExecutionDetail([executionId], apiBase);
    const found = response.executions ?? [];
    if (found.length === 0) return { detail: null, otherIds: [] };
    const primary = found.find(execution => execution.id === executionId) ?? found[found.length - 1];
    return {
      detail: mapExecutionDetail(primary),
      otherIds: found.filter(execution => execution !== primary).map(execution => execution.id),
    };
  };
  return fanOutByRegion(regionBases(getState().env), lookup);
});

/** Points `data` at one region's hit; the sections re-render from it. */
const activate = (state: ExecutionDetailState, region: string | null) => {
  const hit = state.hits.find(h => h.region === region) ?? state.hits[0];
  state.activeRegion = hit?.region ?? null;
  state.data = hit?.detail ?? null;
  state.otherExecutionIds = hit?.otherIds ?? [];
};

const executionDetailSlice = createSlice({
  name: 'executionDetail',
  initialState: executionDetailInitialState,
  reducers: {
    clearExecutionDetail: () => executionDetailInitialState,
    selectExecutionRegion: (state, action: PayloadAction<string>) => {
      activate(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExecutionDetail.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.notFound = false;
        state.currentRequestId = action.meta.requestId;
        state.currentId = action.meta.arg;
      })
      .addCase(fetchExecutionDetail.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        const { hits, searchedRegions, errors } = action.payload;
        state.loading = false;
        state.hits = hits;
        state.searchedRegions = searchedRegions;
        state.regionErrors = errors;
        activate(state, null);
        // Absence is only proven if every region answered; else it is an error.
        state.notFound = hits.length === 0 && errors.length === 0;
        state.error = hits.length === 0 && errors.length > 0
          ? `No region returned an execution. ${describeFailures(errors)}`
          : null;
      })
      .addCase(fetchExecutionDetail.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch execution detail';
      });
  },
});

export const { clearExecutionDetail, selectExecutionRegion } = executionDetailSlice.actions;
export default executionDetailSlice.reducer;
